import { useEffect, useState } from "react";
import {
  MASTER_TEMPLATE_SCROLL_ROOT_ID,
  resolveFeedScrollRoot,
} from "../../../Utils/feedScrollRoot";

/**
 * Gate section network work until the section is near the template scroll
 * viewport (or window). Cascade peers still serialize top→bottom; this stops
 * below-fold strips from competing with Bio/nav once cascade unlocks them.
 *
 * Observes the existing `data-template-section-id` frame from OrderedSectionsRenderer.
 * When `enabled` is false (or no id), always reports near.
 */
export function useSectionNearViewport(
  sectionId: string,
  enabled: boolean,
  rootMargin = "480px 0px",
): boolean {
  const [isNear, setIsNear] = useState(() => !enabled || !sectionId);

  useEffect(() => {
    if (!enabled || !sectionId) {
      setIsNear(true);
      return;
    }

    if (typeof IntersectionObserver === "undefined") {
      setIsNear(true);
      return;
    }

    const escape =
      typeof CSS !== "undefined" && typeof CSS.escape === "function"
        ? CSS.escape
        : (s: string) => s.replace(/"/g, '\\"');

    const findNode = (): HTMLElement | null => {
      const rootEl = resolveFeedScrollRoot(MASTER_TEMPLATE_SCROLL_ROOT_ID);
      const scope =
        rootEl instanceof HTMLElement ? rootEl : document.documentElement;
      return scope.querySelector(
        `[data-template-section-id="${escape(sectionId)}"]`,
      ) as HTMLElement | null;
    };

    let io: IntersectionObserver | null = null;
    let cancelled = false;
    let tries = 0;

    const attach = () => {
      if (cancelled) return;
      const node = findNode();
      if (!node) {
        // Section frame may mount one tick after the carousel body.
        if (tries < 20) {
          tries += 1;
          window.setTimeout(attach, 50);
        } else {
          setIsNear(true);
        }
        return;
      }

      const rootEl = resolveFeedScrollRoot(MASTER_TEMPLATE_SCROLL_ROOT_ID);
      const root = rootEl instanceof HTMLElement ? rootEl : null;
      io = new IntersectionObserver(
        (entries) => {
          if (cancelled) return;
          if (entries.some((e) => e.isIntersecting)) {
            setIsNear(true);
            io?.disconnect();
          }
        },
        { root, rootMargin },
      );
      io.observe(node);
    };

    setIsNear(false);
    attach();

    return () => {
      cancelled = true;
      io?.disconnect();
    };
  }, [enabled, sectionId, rootMargin]);

  return enabled && sectionId ? isNear : true;
}
